// SNIGJ - Business Intelligence & Advanced Statistics Dashboard
import React, { useState, useEffect } from 'react';
import { useAuth } from '../context/AuthContext';
import DashboardLayout from '../components/layout/DashboardLayout';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { 
  BarChart3, TrendingUp, TrendingDown, Users, FileText, DollarSign, 
  Calendar, Download, RefreshCw, PieChart, Activity, MapPin,
  Scale, AlertTriangle, Clock, CheckCircle, Building2
} from 'lucide-react';
import { 
  BarChart, Bar, LineChart, Line, PieChart as RechartsPie, Pie, Cell,
  XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer,
  AreaChart, Area, ComposedChart
} from 'recharts';
import api from '../lib/api';

const COLORS = ['#eab308', '#3b82f6', '#10b981', '#ef4444', '#8b5cf6', '#f97316'];

const StatisticsPage = () => {
  const { language } = useAuth();
  const [loading, setLoading] = useState(true);
  const [period, setPeriod] = useState('month');
  const [stats, setStats] = useState(null);
  const [caseStats, setCaseStats] = useState([]);
  const [paymentStats, setPaymentStats] = useState([]);
  const [jurisdictionStats, setJurisdictionStats] = useState([]);
  const [trendData, setTrendData] = useState([]);

  useEffect(() => {
    loadAllStats();
  }, [period]);

  const loadAllStats = async () => {
    setLoading(true);
    try {
      const [overviewRes, casesRes, paymentsRes, jurisdictionsRes, trendsRes] = await Promise.all([
        api.get(`/statistics/overview?period=${period}`),
        api.get(`/statistics/cases?period=${period}`),
        api.get(`/statistics/payments?period=${period}`),
        api.get(`/statistics/jurisdictions?period=${period}`),
        api.get(`/statistics/trends?period=${period}`)
      ]);

      setStats(overviewRes.data);
      setCaseStats(casesRes.data.stats || []);
      setPaymentStats(paymentsRes.data.stats || []);
      setJurisdictionStats(jurisdictionsRes.data.stats || []);
      setTrendData(trendsRes.data.trends || []);
    } catch (error) {
      console.error('Failed to load stats:', error);
      // Use mock data for demo
      setStats(MOCK_OVERVIEW);
      setCaseStats(MOCK_CASE_STATS);
      setPaymentStats(MOCK_PAYMENT_STATS);
      setJurisdictionStats(MOCK_JURISDICTION_STATS);
      setTrendData(MOCK_TRENDS);
    } finally {
      setLoading(false);
    }
  };

  const exportReport = async (format) => {
    try {
      const response = await api.get(`/statistics/export?format=${format}&period=${period}`, {
        responseType: 'blob'
      });
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `snigj_report_${period}.${format}`);
      document.body.appendChild(link);
      link.click();
      link.remove();
    } catch (error) {
      console.error('Export failed:', error);
    }
  };

  if (loading) {
    return (
      <DashboardLayout>
        <div className="space-y-6">
          <div className="h-8 w-64 skeleton" />
          <div className="grid grid-cols-4 gap-4">
            {[1,2,3,4].map(i => <div key={i} className="h-32 skeleton" />)}
          </div>
          <div className="grid grid-cols-2 gap-4">
            {[1,2].map(i => <div key={i} className="h-80 skeleton" />)}
          </div>
        </div>
      </DashboardLayout>
    );
  }

  return (
    <DashboardLayout>
      <div className="space-y-6" data-testid="statistics-page">
        {/* Header */}
        <div className="flex items-center justify-between">
          <div>
            <h1 className="font-heading text-3xl font-bold tracking-tight flex items-center gap-3">
              <BarChart3 className="w-8 h-8 text-primary" />
              {language === 'fr' ? 'Statistiques & Analyses' : 'Statistics & Analytics'}
            </h1>
            <p className="text-muted-foreground mt-1">
              {language === 'fr' ? 'Tableau de bord analytique du système judiciaire' : 'Judicial system analytics dashboard'}
            </p>
          </div>
          <div className="flex items-center gap-3">
            <Select value={period} onValueChange={setPeriod}>
              <SelectTrigger className="w-[150px]">
                <Calendar className="w-4 h-4 mr-2" />
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="week">{language === 'fr' ? 'Cette semaine' : 'This Week'}</SelectItem>
                <SelectItem value="month">{language === 'fr' ? 'Ce mois' : 'This Month'}</SelectItem>
                <SelectItem value="quarter">{language === 'fr' ? 'Ce trimestre' : 'This Quarter'}</SelectItem>
                <SelectItem value="year">{language === 'fr' ? 'Cette année' : 'This Year'}</SelectItem>
              </SelectContent>
            </Select>
            <Button variant="outline" onClick={() => loadAllStats()}>
              <RefreshCw className="w-4 h-4 mr-2" />
              {language === 'fr' ? 'Actualiser' : 'Refresh'}
            </Button>
            <Button onClick={() => exportReport('pdf')}>
              <Download className="w-4 h-4 mr-2" />
              {language === 'fr' ? 'Exporter' : 'Export'}
            </Button>
          </div>
        </div>

        {/* KPI Cards */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          <Card>
            <CardContent className="p-4">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-muted-foreground">{language === 'fr' ? 'Total Dossiers' : 'Total Cases'}</p>
                  <p className="text-3xl font-bold">{stats?.total_cases || 0}</p>
                  <div className="flex items-center gap-1 text-xs mt-1">
                    {stats?.cases_change >= 0 ? (
                      <TrendingUp className="w-3 h-3 text-green-500" />
                    ) : (
                      <TrendingDown className="w-3 h-3 text-red-500" />
                    )}
                    <span className={stats?.cases_change >= 0 ? 'text-green-500' : 'text-red-500'}>
                      {stats?.cases_change || 0}%
                    </span>
                  </div>
                </div>
                <div className="w-12 h-12 rounded-lg bg-blue-100 dark:bg-blue-900/30 flex items-center justify-center">
                  <FileText className="w-6 h-6 text-blue-600" />
                </div>
              </div>
            </CardContent>
          </Card>

          <Card>
            <CardContent className="p-4">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-muted-foreground">{language === 'fr' ? 'Détenus' : 'Prisoners'}</p>
                  <p className="text-3xl font-bold">{stats?.total_prisoners || 0}</p>
                  <div className="flex items-center gap-1 text-xs mt-1">
                    <span className="text-muted-foreground">{stats?.prisoners_released || 0} {language === 'fr' ? 'libérés' : 'released'}</span>
                  </div>
                </div>
                <div className="w-12 h-12 rounded-lg bg-orange-100 dark:bg-orange-900/30 flex items-center justify-center">
                  <Users className="w-6 h-6 text-orange-600" />
                </div>
              </div>
            </CardContent>
          </Card>

          <Card>
            <CardContent className="p-4">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-muted-foreground">{language === 'fr' ? 'Revenus' : 'Revenue'}</p>
                  <p className="text-3xl font-bold">${(stats?.total_revenue || 0).toLocaleString()}</p>
                  <div className="flex items-center gap-1 text-xs mt-1">
                    {stats?.revenue_change >= 0 ? (
                      <TrendingUp className="w-3 h-3 text-green-500" />
                    ) : (
                      <TrendingDown className="w-3 h-3 text-red-500" />
                    )}
                    <span className={stats?.revenue_change >= 0 ? 'text-green-500' : 'text-red-500'}>
                      {stats?.revenue_change || 0}%
                    </span>
                  </div>
                </div>
                <div className="w-12 h-12 rounded-lg bg-green-100 dark:bg-green-900/30 flex items-center justify-center">
                  <DollarSign className="w-6 h-6 text-green-600" />
                </div>
              </div>
            </CardContent>
          </Card>

          <Card>
            <CardContent className="p-4">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-muted-foreground">{language === 'fr' ? 'Taux Résolution' : 'Resolution Rate'}</p>
                  <p className="text-3xl font-bold">{stats?.resolution_rate || 0}%</p>
                  <div className="flex items-center gap-1 text-xs mt-1">
                    <CheckCircle className="w-3 h-3 text-green-500" />
                    <span className="text-muted-foreground">{stats?.resolved_cases || 0} {language === 'fr' ? 'résolus' : 'resolved'}</span>
                  </div>
                </div>
                <div className="w-12 h-12 rounded-lg bg-purple-100 dark:bg-purple-900/30 flex items-center justify-center">
                  <Scale className="w-6 h-6 text-purple-600" />
                </div>
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Charts Row 1 */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          {/* Case Trends */}
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Activity className="w-5 h-5" />
                {language === 'fr' ? 'Évolution des Dossiers' : 'Case Trends'}
              </CardTitle>
            </CardHeader>
            <CardContent>
              <ResponsiveContainer width="100%" height={300}>
                <AreaChart data={trendData}>
                  <CartesianGrid strokeDasharray="3 3" />
                  <XAxis dataKey="name" />
                  <YAxis />
                  <Tooltip />
                  <Legend />
                  <Area type="monotone" dataKey="nouveaux" stackId="1" stroke="#3b82f6" fill="#3b82f6" name={language === 'fr' ? 'Nouveaux' : 'New'} />
                  <Area type="monotone" dataKey="resolus" stackId="1" stroke="#10b981" fill="#10b981" name={language === 'fr' ? 'Résolus' : 'Resolved'} />
                  <Area type="monotone" dataKey="en_cours" stackId="1" stroke="#eab308" fill="#eab308" name={language === 'fr' ? 'En cours' : 'Ongoing'} />
                </AreaChart>
              </ResponsiveContainer>
            </CardContent>
          </Card>

          {/* Case Types Distribution */}
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <PieChart className="w-5 h-5" />
                {language === 'fr' ? 'Répartition par Type' : 'Distribution by Type'}
              </CardTitle>
            </CardHeader>
            <CardContent>
              <ResponsiveContainer width="100%" height={300}>
                <RechartsPie>
                  <Pie
                    data={caseStats}
                    cx="50%"
                    cy="50%"
                    labelLine={false}
                    label={({ name, percent }) => `${name} ${(percent * 100).toFixed(0)}%`}
                    outerRadius={100}
                    fill="#8884d8"
                    dataKey="value"
                  >
                    {caseStats.map((entry, index) => (
                      <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
                    ))}
                  </Pie>
                  <Tooltip />
                </RechartsPie>
              </ResponsiveContainer>
            </CardContent>
          </Card>
        </div>

        {/* Charts Row 2 */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          {/* Payment Stats */}
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <DollarSign className="w-5 h-5" />
                {language === 'fr' ? 'Revenus par Catégorie' : 'Revenue by Category'}
              </CardTitle>
            </CardHeader>
            <CardContent>
              <ResponsiveContainer width="100%" height={300}>
                <BarChart data={paymentStats}>
                  <CartesianGrid strokeDasharray="3 3" />
                  <XAxis dataKey="name" />
                  <YAxis />
                  <Tooltip />
                  <Legend />
                  <Bar dataKey="amount" fill="#eab308" name={language === 'fr' ? 'Montant ($)' : 'Amount ($)'} />
                  <Bar dataKey="count" fill="#3b82f6" name={language === 'fr' ? 'Nombre' : 'Count'} />
                </BarChart>
              </ResponsiveContainer>
            </CardContent>
          </Card>

          {/* Jurisdiction Performance */}
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Building2 className="w-5 h-5" />
                {language === 'fr' ? 'Performance par Juridiction' : 'Performance by Jurisdiction'}
              </CardTitle>
            </CardHeader>
            <CardContent>
              <ResponsiveContainer width="100%" height={300}>
                <ComposedChart data={jurisdictionStats}>
                  <CartesianGrid strokeDasharray="3 3" />
                  <XAxis dataKey="name" />
                  <YAxis yAxisId="left" />
                  <YAxis yAxisId="right" orientation="right" />
                  <Tooltip />
                  <Legend />
                  <Bar yAxisId="left" dataKey="cases" fill="#3b82f6" name={language === 'fr' ? 'Dossiers' : 'Cases'} />
                  <Line yAxisId="right" type="monotone" dataKey="resolution_rate" stroke="#10b981" name={language === 'fr' ? 'Taux (%)' : 'Rate (%)'} />
                </ComposedChart>
              </ResponsiveContainer>
            </CardContent>
          </Card>
        </div>

        {/* Additional Stats */}
        <Card>
          <CardHeader>
            <CardTitle>{language === 'fr' ? 'Indicateurs Clés' : 'Key Indicators'}</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
              <div className="p-4 bg-muted rounded-lg">
                <p className="text-sm text-muted-foreground">{language === 'fr' ? 'Temps moyen de traitement' : 'Avg Processing Time'}</p>
                <p className="text-2xl font-bold">{stats?.avg_processing_days || 0} {language === 'fr' ? 'jours' : 'days'}</p>
              </div>
              <div className="p-4 bg-muted rounded-lg">
                <p className="text-sm text-muted-foreground">{language === 'fr' ? 'Audiences ce mois' : 'Hearings this month'}</p>
                <p className="text-2xl font-bold">{stats?.hearings_this_month || 0}</p>
              </div>
              <div className="p-4 bg-muted rounded-lg">
                <p className="text-sm text-muted-foreground">{language === 'fr' ? 'Documents générés' : 'Documents generated'}</p>
                <p className="text-2xl font-bold">{stats?.documents_generated || 0}</p>
              </div>
              <div className="p-4 bg-muted rounded-lg">
                <p className="text-sm text-muted-foreground">{language === 'fr' ? 'Alertes frontières' : 'Border alerts'}</p>
                <p className="text-2xl font-bold">{stats?.border_alerts || 0}</p>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>
    </DashboardLayout>
  );
};

// Mock data for demo
const MOCK_OVERVIEW = {
  total_cases: 1247,
  cases_change: 12,
  total_prisoners: 3421,
  prisoners_released: 156,
  total_revenue: 89500,
  revenue_change: 8,
  resolution_rate: 67,
  resolved_cases: 835,
  avg_processing_days: 45,
  hearings_this_month: 234,
  documents_generated: 1589,
  border_alerts: 23
};

const MOCK_CASE_STATS = [
  { name: 'Civil', value: 450 },
  { name: 'Pénal', value: 380 },
  { name: 'Commercial', value: 220 },
  { name: 'Administratif', value: 120 },
  { name: 'Famille', value: 77 }
];

const MOCK_PAYMENT_STATS = [
  { name: 'Frais dossier', amount: 25000, count: 450 },
  { name: 'Grâce', amount: 15000, count: 150 },
  { name: 'Libération', amount: 12000, count: 240 },
  { name: 'Certificats', amount: 8500, count: 567 },
  { name: 'Appels', amount: 29000, count: 387 }
];

const MOCK_JURISDICTION_STATS = [
  { name: 'TGI Kinshasa', cases: 450, resolution_rate: 72 },
  { name: 'TGI Lubumbashi', cases: 280, resolution_rate: 65 },
  { name: 'TGI Goma', cases: 190, resolution_rate: 58 },
  { name: 'TGI Matadi', cases: 145, resolution_rate: 70 },
  { name: 'TGI Kisangani', cases: 120, resolution_rate: 62 }
];

const MOCK_TRENDS = [
  { name: 'Jan', nouveaux: 120, resolus: 85, en_cours: 35 },
  { name: 'Fév', nouveaux: 145, resolus: 92, en_cours: 53 },
  { name: 'Mar', nouveaux: 130, resolus: 110, en_cours: 20 },
  { name: 'Avr', nouveaux: 165, resolus: 125, en_cours: 40 },
  { name: 'Mai', nouveaux: 155, resolus: 140, en_cours: 15 },
  { name: 'Juin', nouveaux: 180, resolus: 150, en_cours: 30 }
];

export default StatisticsPage;
